import { useState, useEffect } from 'react';
import { addDoc, updateDoc, collection, doc } from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import { Modal, ModalBanner, Button, FormField } from './ui';
import type { Reminder, ReminderType } from '../types';

// Local calendar day (not UTC), see CLAUDE.md "Date-only strings".
function todayISO() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }
function stripUndef<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}

export function fmtTime(t: string): string {
  const [h, m] = t.split(':').map(Number);
  if (isNaN(h)) return t;
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m ?? 0).padStart(2, '0')} ${period}`;
}

// ── Icons (reminder-category only — Dashboard keeps its own copies of the
// two icons it also reuses for Needs Attention: IconMemoTag, IconShieldCheck) ──

function IconPayment() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.3" />
    <path d="M7 4v6M5.3 5.3c0-.7.76-1.3 1.7-1.3.94 0 1.7.6 1.7 1.3 0 .7-.76 1-1.7 1.15-.94.15-1.7.45-1.7 1.15 0 .7.76 1.3 1.7 1.3.94 0 1.7-.6 1.7-1.3"
      stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconMemoTag() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M1.5 1.5h5L12.5 8l-5 5-6-6v-5z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    <circle cx="4.2" cy="4.2" r="0.9" fill="currentColor" />
  </svg>;
}
function IconTruck() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <rect x="1" y="4" width="7" height="6" rx="0.8" stroke="currentColor" strokeWidth="1.2" />
    <path d="M8 6.5h2.5L12.5 8.5V10H8V6.5z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    <circle cx="3.7" cy="11" r="1.1" stroke="currentColor" strokeWidth="1.1" />
    <circle cx="10.3" cy="11" r="1.1" stroke="currentColor" strokeWidth="1.1" />
  </svg>;
}
function IconCertificate() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="5" r="3.5" stroke="currentColor" strokeWidth="1.2" />
    <path d="M4.8 8l-1 4.5 3.2-1.8 3.2 1.8-1-4.5" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" />
    <path d="M5.3 5l1.2 1.2L8.8 3.7" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconShieldCheck() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M7 1.5l4.5 1.6v3.4c0 3-1.9 5.3-4.5 6-2.6-.7-4.5-3-4.5-6V3.1L7 1.5z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    <path d="M4.8 7l1.4 1.4 2.8-2.8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconMeeting() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <circle cx="5" cy="4.5" r="2" stroke="currentColor" strokeWidth="1.2" />
    <circle cx="10" cy="5.5" r="1.6" stroke="currentColor" strokeWidth="1.1" />
    <path d="M1.2 12c0-2.1 1.7-3.6 3.8-3.6s3.8 1.5 3.8 3.6M9 9.3c1.7.2 2.9 1.4 2.9 3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>;
}
function IconDot() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.3" />
    <circle cx="7" cy="7" r="1.5" fill="currentColor" />
  </svg>;
}

// Tailored to how this business actually operates — each category ties to a
// real workflow already tracked elsewhere in the app, rather than generic
// "Call / Appointment" labels: Payment mirrors Accounts Payable/Receivable,
// Memo mirrors Inventory's on-memo status, Certification mirrors Lab
// entities, KYC mirrors Entity KYC tracking.
export const REMINDER_TYPE_MAP: Record<ReminderType, { label: string; icon: React.ReactNode; color: string; bg: string }> = {
  payment:       { label: 'Payment Follow-up',     icon: <IconPayment />,      color: '#059669', bg: '#ECFDF5' },
  memo:          { label: 'Memo Follow-up',        icon: <IconMemoTag />,      color: '#D97706', bg: '#FFFBEB' },
  shipment:      { label: 'Shipment / Delivery',   icon: <IconTruck />,        color: '#2563EB', bg: '#EFF6FF' },
  meeting:       { label: 'Client / Supplier Meeting', icon: <IconMeeting />,  color: '#7C3AED', bg: '#F5F3FF' },
  certification: { label: 'Certification / Lab',   icon: <IconCertificate />, color: '#0891B2', bg: '#ECFEFF' },
  kyc:           { label: 'KYC / Compliance',      icon: <IconShieldCheck />, color: '#DC2626', bg: '#FEF2F2' },
  other:         { label: 'Other',                 icon: <IconDot />,         color: 'var(--text-3)', bg: 'var(--surface-2)' },
};

// ── Add / Edit Reminder modal — shared by Dashboard's "Today's Reminders"
// card and the full Reminders page, so the two never drift out of sync. ──

export function AddReminderModal({ open, onClose, editing }: { open: boolean; onClose: () => void; editing: Reminder | null }) {
  const { user, business } = useAuth();
  const EMPTY = { title: '', description: '', date: todayISO(), time: '', type: '' as ReminderType | '' };
  const [form,   setForm]   = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  const isEdit = !!editing;

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        title: editing.title,
        description: editing.description ?? '',
        date: editing.date,
        time: editing.time,
        type: editing.type ?? '',
      });
    } else {
      setForm({ ...EMPTY, date: todayISO() });
    }
    setError('');
  }, [open, editing]); // eslint-disable-line react-hooks/exhaustive-deps

  function set<K extends keyof typeof EMPTY>(k: K, v: typeof EMPTY[K]) { setForm(f => ({ ...f, [k]: v })); }

  function validate(): string {
    if (!form.title.trim()) return 'Enter a title.';
    if (!form.date) return 'Select a date.';
    if (!form.time) return 'Select a time.';
    return '';
  }

  async function handleSave() {
    const err = validate();
    if (err) { setError(err); return; }
    setSaving(true); setError('');
    const db = getClientDb();
    if (!db) { setError('Database not connected.'); setSaving(false); return; }
    try {
      const payload = stripUndef({
        title:       form.title.trim(),
        description: form.description.trim() || undefined,
        date:        form.date,
        time:        form.time,
        type:        form.type || undefined,
      });
      // Don't wait for the server: Firestore shows the write in every open
      // list straight away and sends it in the background, so the modal
      // closes at once instead of hanging until the server confirms.
      const write = isEdit && editing
        ? updateDoc(doc(db, 'reminders', editing.id), payload)
        : addDoc(collection(db, 'reminders'), stripUndef({
            ...payload,
            businessId: business!.id,
            createdAt:  new Date().toISOString(),
            createdBy:  user!.id,
          }));
      write.catch(e => console.error('[Reminders] save failed:', e));
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save reminder.');
    }
    setSaving(false);
  }

  return (
    <Modal
      open={open} onClose={onClose} hideHead size="sm"
      footer={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
          {error && <div className="msg msg-error" style={{ margin: 0 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} loading={saving}>{isEdit ? 'Save Changes' : 'Add Reminder'}</Button>
          </div>
        </div>
      }
    >
      <ModalBanner
        title={form.title.trim() || (isEdit ? 'Edit Reminder' : 'New Reminder')}
        eyebrow={isEdit ? 'Edit Reminder' : 'Add Reminder'}
        onClose={onClose}
      />
      <p className="form-hint" style={{ marginTop: -12, marginBottom: 14 }}>Shows on the Dashboard on the day it's due.</p>

      <FormField label="Title" required>
        <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Call Rakesh about payment" autoFocus />
      </FormField>
      <div className="form-row-2">
        <FormField label="Date" required>
          <input type="date" value={form.date} onChange={e => set('date', e.target.value)} />
        </FormField>
        <FormField label="Time" required>
          <input type="time" value={form.time} onChange={e => set('time', e.target.value)} />
        </FormField>
      </div>
      <FormField label="Type (optional)">
        <select value={form.type} onChange={e => set('type', e.target.value as ReminderType | '')}>
          <option value="">No category</option>
          {(Object.keys(REMINDER_TYPE_MAP) as ReminderType[]).map(t => (
            <option key={t} value={t}>{REMINDER_TYPE_MAP[t].label}</option>
          ))}
        </select>
      </FormField>
      <FormField label="Description (optional)">
        <textarea value={form.description} onChange={e => set('description', e.target.value)} placeholder="Any notes…" style={{ minHeight: 60 }} />
      </FormField>
    </Modal>
  );
}
